import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../lib/api";

const PAGE_SIZE = 40;

const clean = (obj) => Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined && v !== null && v !== ""));

// Server-side board of the Pipeline page: per-column exact counts + keyset pages (`GET /prospects/board`), the action counters
// (`GET /prospects/board/counters`, a second request so the cards show first), a silent reload that never blanks the screen, and
// local moves that can be reverted. Every answer is checked against the request it belongs to, so a slow stale one never lands.
export default function usePipelineBoard({ scope, phase, stages, q, channel, pilot, flag, sort, assignee }) {
  const [columns, setColumns] = useState({});
  const [meta, setMeta] = useState({ scopeCounts: null, assignees: {}, tasksVisible: false, effectiveScope: null });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(false);
  const [counters, setCounters] = useState({ status: "loading", data: null });
  const [version, setVersion] = useState(0);
  const columnsRef = useRef({});
  const requestId = useRef(0);
  const counterRequestId = useRef(0);
  const loaded = useRef(false);

  const stageKey = stages.join(",");
  const boardParams = useMemo(() => clean({ scope, stages: stageKey, q, channel, pilot, flag, sort, assignee, limit: PAGE_SIZE }), [scope, stageKey, q, channel, pilot, flag, sort, assignee]);

  const store = useCallback((next) => {
    columnsRef.current = next;
    setColumns(next);
  }, []);

  useEffect(() => {
    const id = ++requestId.current;
    if (loaded.current) setRefreshing(true); else setLoading(true);
    setError(false);
    api.get("/prospects/board", { params: boardParams })
      .then(({ data }) => {
        if (id !== requestId.current) return;
        const next = {};
        for (const [stage, value] of Object.entries(data.stages)) next[stage] = { count: value.count, items: value.items, next: value.next, loadingMore: false };
        store(next);
        setMeta({ scopeCounts: data.scope_counts, assignees: data.assignees || {}, tasksVisible: !!data.tasks_visible, effectiveScope: data.effective_scope });
        loaded.current = true;
      })
      .catch(() => { if (id === requestId.current) setError(true); })
      .finally(() => { if (id === requestId.current) { setLoading(false); setRefreshing(false); } });
  }, [boardParams, version, store]);

  // Counters depend on Phase x Scope only: typing in the search never moves them.
  useEffect(() => {
    const id = ++counterRequestId.current;
    setCounters((c) => ({ status: c.data ? "refreshing" : "loading", data: c.data }));
    api.get("/prospects/board/counters", { params: clean({ scope, phase }) })
      .then(({ data }) => { if (id === counterRequestId.current) setCounters({ status: "ok", data }); })
      .catch(() => { if (id === counterRequestId.current) setCounters({ status: "error", data: null }); });
  }, [scope, phase, version]);

  const reload = useCallback(() => setVersion((v) => v + 1), []);

  const loadMore = useCallback(async (stage) => {
    const column = columnsRef.current[stage];
    if (!column?.next || column.loadingMore) return;
    store({ ...columnsRef.current, [stage]: { ...column, loadingMore: true } });
    try {
      const { data } = await api.get("/prospects/board", { params: { ...boardParams, stages: stage, after: column.next } });
      const page = data.stages[stage];
      const current = columnsRef.current[stage];
      const known = new Set(current.items.map((c) => c.id));
      store({ ...columnsRef.current, [stage]: { ...current, items: [...current.items, ...page.items.filter((c) => !known.has(c.id))], next: page.next, count: page.count, loadingMore: false } });
    } catch (e) {
      store({ ...columnsRef.current, [stage]: { ...columnsRef.current[stage], loadingMore: false, loadMoreFailed: true } });
    }
  }, [boardParams, store]);

  // Targeted refresh of given cards (after an action or a conflict): relocates them to their real column, drops the vanished ones.
  const refreshCards = useCallback(async (ids) => {
    const { data } = await api.get("/prospects/board", { params: { ids: ids.join(",") } });
    const next = { ...columnsRef.current };
    const strip = (id) => {
      for (const stage of Object.keys(next)) {
        if (next[stage].items.some((c) => c.id === id)) next[stage] = { ...next[stage], items: next[stage].items.filter((c) => c.id !== id), count: Math.max(0, next[stage].count - 1) };
      }
    };
    for (const id of data.missing) strip(id);
    for (const card of data.items) {
      strip(card.id);
      if (next[card.status]) next[card.status] = { ...next[card.status], items: [card, ...next[card.status].items], count: next[card.status].count + 1 };
    }
    store(next);
    return data;
  }, [store]);

  // Local move (optimistic): returns a snapshot to revert to.
  const moveLocal = useCallback((id, target, patch = {}) => {
    const before = columnsRef.current;
    const next = { ...before };
    let card = null;
    for (const stage of Object.keys(next)) {
      const found = next[stage].items.find((c) => c.id === id);
      if (found) {
        card = found;
        next[stage] = { ...next[stage], items: next[stage].items.filter((c) => c.id !== id), count: Math.max(0, next[stage].count - 1) };
      }
    }
    if (card && next[target]) next[target] = { ...next[target], items: [{ ...card, ...patch, status: target }, ...next[target].items], count: next[target].count + 1 };
    store(next);
    return before;
  }, [store]);

  const restore = useCallback((snapshot) => store(snapshot), [store]);

  return { columns, meta, loading, refreshing, error, counters, reload, loadMore, refreshCards, moveLocal, restore, pageSize: PAGE_SIZE };
}
